import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuPencil, LuRotateCcw, LuSparkles, LuAlertTriangle } from 'react-icons/lu';
import { Button, PersonChip, SegmentedControl, confirm } from 'components/common';
import { INPUT_CLS } from 'helpers/formStyles';
import { formatDate } from 'helpers/utils';
import {
  FILTERS, PROPOSAL_SOURCE_LABEL, STATUS_META, attentionCount, buildDraft, draftToPayload, groupRows, updateDraft,
} from './pifCheckModel';

const TH_CLS = 'tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500';
const TD_CLS = 'tw-px-3 tw-py-2 tw-text-sm tw-align-top';

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.not_specified;
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-semibold tw-border tw-border-solid ${meta.cls}`}>
      {meta.short}
    </span>
  );
};
StatusPill.propTypes = { status: PropTypes.string };

const Value = ({ value }) => (
  value == null || value === ''
    ? <span className="tw-text-slate-300">—</span>
    : <span className="tw-text-slate-800 tw-whitespace-pre-wrap tw-break-words">{value}</span>
);
Value.propTypes = { value: PropTypes.string };

const SummaryLine = ({ summary }) => (
  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
    {['mismatch', 'review', 'missing_in_proposal', 'missing_in_pif', 'match'].filter((s) => summary?.[s] > 0).map((status) => (
      <span key={status} className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-semibold tw-border tw-border-solid ${STATUS_META[status].cls}`}>
        {summary[status]} {STATUS_META[status].label.toLowerCase()}
      </span>
    ))}
  </div>
);
SummaryLine.propTypes = { summary: PropTypes.object };

const ReviewState = ({ check }) => {
  if (!check?.reviewed_at) return <span className="tw-text-xs tw-text-slate-400">Not checked yet</span>;

  return (
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-600">
      {check.review_stale ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-amber-700 tw-font-semibold"
          title="The PIF, the CO# or the proposal values changed after this check was confirmed">
          <LuAlertTriangle size={13} /> Changed since checked
        </span>
      ) : (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-emerald-700 tw-font-semibold"><LuCheck size={13} /> Checked</span>
      )}
      {check.reviewed_by && (
        <PersonChip name={check.reviewed_by.name} fullName={check.reviewed_by.full_name} email={check.reviewed_by.email} size="xs" />
      )}
      <span className="tw-text-slate-400">{formatDate(check.reviewed_at)}</span>
    </span>
  );
};
ReviewState.propTypes = { check: PropTypes.object };

// Step 3, one building: the comparison table (per area, per section) against the
// PIF of the CO# chosen in step 1, and the review action. Proposal values are editable here because the AI can misread a
// scan — the person checking corrects them and the comparison re-runs on save.
const BuildingPifCheck = ({ building, fields, sections, canEdit, editLocked, onSave, onLoadFromAi, onReview }) => {
  const [filter, setFilter] = useState('attention');
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);
  const busyRef = useRef(false);

  const selected = building.change_orders.find((co) => co.adaptation_id === building.selected_adaptation_id);
  const attention = attentionCount(building.summary);
  const editing = draft !== null;
  const rowFilter = editing ? FILTERS.all : FILTERS[filter];
  const hasProposal = building.proposal_source !== 'none';
  const reviewed = !!building.check?.reviewed_at;

  const run = async (key, fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(key);
    try {
      await fn();
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const areaGroups = useMemo(
    () => building.areas.map((area) => ({ area, groups: groupRows(area.rows, sections, rowFilter) })),
    [building.areas, sections, rowFilter],
  );

  const saveDraft = () => run('save', async () => {
    const saved = await onSave(building, { proposal_areas: draftToPayload(draft) });
    if (saved) setDraft(null);
  });

  const loadAi = () => run('ai', async () => {
    const ok = await confirm({
      title: 'Replace with AI values?',
      message: `The proposal values of ${building.building_name} will be replaced by what the AI read from the file. Your manual edits will be lost.`,
      confirmText: 'Replace',
    });
    if (ok) await onLoadFromAi(building);
  });

  const review = () => run('review', async () => {
    if (attention > 0) {
      const ok = await confirm({
        title: 'Confirm with open differences?',
        message: `${attention} field(s) still differ or need a look. Confirm the check anyway?`,
        confirmText: 'Confirm check',
      });
      if (!ok) return;
    }
    await onReview(building, { adaptationId: building.selected_adaptation_id });
  });

  const reopen = () => run('reopen', () => onReview(building, { reopen: true }));

  return (
    <section className="tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg tw-bg-white">
      <header className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
        <h4 className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-m-0">{building.building_name}</h4>

        {/* The CO# is chosen in step 1; here it only says what the proposal is held against. */}
        <span className="tw-text-xs tw-text-slate-500">
          vs PIF <strong className="tw-text-slate-700">{selected?.label || '—'}</strong>
        </span>

        <span className="tw-text-[11px] tw-text-slate-500">Proposal: {PROPOSAL_SOURCE_LABEL[building.proposal_source]}</span>

        <div className="tw-ml-auto"><ReviewState check={building.check} /></div>
      </header>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-px-4 tw-py-2.5 tw-bg-slate-50 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
        {editing ? (
          <span className="tw-text-xs tw-text-slate-600">Editing the proposal values — the comparison refreshes when you save.</span>
        ) : (
          <>
            <SegmentedControl size="sm" value={filter} onChange={setFilter} ariaLabel="Rows to show"
              options={[
                { value: 'attention', label: 'Needs attention', count: attention },
                { value: 'all', label: 'All fields', count: building.summary?.total },
              ]} />
            <SummaryLine summary={building.summary} />
          </>
        )}

        {canEdit && (
          <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
            {editing ? (
              <>
                <Button type="button" variant="secondary" size="sm" onClick={() => setDraft(null)} disabled={busy !== null}>Cancel</Button>
                <Button type="button" size="sm" onClick={saveDraft} loading={busy === 'save'}>Save values</Button>
              </>
            ) : (
              <>
                {building.ai_available && building.proposal_source !== 'ai_unsaved' && (
                  <Button type="button" variant="ghost" size="sm" leftIcon={LuSparkles} onClick={loadAi} loading={busy === 'ai'}
                    title="Replace the stored values with what the AI read from the proposal file">
                    Use AI values
                  </Button>
                )}
                <Button type="button" variant="secondary" size="sm" leftIcon={LuPencil}
                  onClick={() => setDraft(buildDraft(building.areas, fields))}
                  disabled={busy !== null || editLocked || building.areas.length === 0}
                  title={editLocked ? 'Wait until the AI has finished reading the proposal' : undefined}>
                  Edit proposal values
                </Button>
                {reviewed ? (
                  <Button type="button" variant="secondary" size="sm" leftIcon={LuRotateCcw} onClick={reopen} loading={busy === 'reopen'}>
                    {building.check.review_stale ? 'Reopen' : 'Undo check'}
                  </Button>
                ) : null}
                {(!reviewed || building.check.review_stale) && (
                  <Button type="button" size="sm" leftIcon={LuCheck} onClick={review} loading={busy === 'review'}
                    disabled={!hasProposal || !selected}
                    title={!hasProposal ? 'Upload the proposal or type its values first' : undefined}>
                    {building.check?.review_stale ? 'Re-confirm check' : 'Mark as checked'}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {building.areas.length === 0 ? (
        <p className="tw-px-4 tw-py-6 tw-text-sm tw-text-slate-400 tw-text-center tw-mb-0">
          {selected ? 'The PIF of this CO# has no areas.' : 'This building has no CO# with a PIF yet.'}
        </p>
      ) : (
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-border-collapse">
            <colgroup>
              <col style={{ width: '30%' }} /><col style={{ width: '28%' }} /><col style={{ width: '30%' }} /><col style={{ width: '12%' }} />
            </colgroup>
            <thead className="tw-bg-white">
              <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
                <th className={TH_CLS}>Field</th>
                <th className={TH_CLS}>PIF {selected?.label}</th>
                <th className={TH_CLS}>Proposal</th>
                <th className={TH_CLS}>Result</th>
              </tr>
            </thead>
            {areaGroups.map(({ area, groups }, areaIndex) => (
              <tbody key={`area-${area.area_no}-${areaIndex}`}>
                <tr className="tw-bg-slate-100">
                  <td colSpan={4} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700">
                    Area No. {area.area_no}
                    {!area.in_pif && <span className="tw-ml-2 tw-text-amber-700 tw-font-medium">only in proposal</span>}
                    {!area.in_proposal && area.in_pif && hasProposal && <span className="tw-ml-2 tw-text-amber-700 tw-font-medium">not in proposal</span>}
                  </td>
                </tr>
                {groups.length === 0 && (
                  <tr><td colSpan={4} className="tw-px-3 tw-py-3 tw-text-xs tw-text-emerald-700">Nothing to look at in this area.</td></tr>
                )}
                {groups.map((group) => (
                  <React.Fragment key={group.section}>
                    <tr>
                      <td colSpan={4} className="tw-px-3 tw-pt-3 tw-pb-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
                        {group.label}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.key} className={`tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 ${row.status === 'mismatch' ? 'tw-bg-red-50/40' : ''}`}>
                        <td className={`${TD_CLS} tw-text-slate-600`}>{row.label}</td>
                        <td className={TD_CLS}><Value value={row.pif} /></td>
                        <td className={TD_CLS}>
                          {editing ? (
                            <input className={`${INPUT_CLS} !tw-py-1`} aria-label={`${row.label} — proposal, area ${area.area_no}`}
                              value={draft[areaIndex]?.[row.key] ?? ''}
                              onChange={(e) => setDraft((prev) => updateDraft(prev, areaIndex, row.key, e.target.value))} />
                          ) : <Value value={row.proposal} />}
                        </td>
                        <td className={TD_CLS}><StatusPill status={row.status} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
};

BuildingPifCheck.propTypes = {
  building:     PropTypes.object.isRequired,
  fields:       PropTypes.array.isRequired,
  sections:     PropTypes.object.isRequired,
  canEdit:      PropTypes.bool,
  editLocked:   PropTypes.bool,
  onSave:       PropTypes.func.isRequired,
  onLoadFromAi: PropTypes.func.isRequired,
  onReview:     PropTypes.func.isRequired,
};

export default BuildingPifCheck;
